import React from 'react';

import debounce from 'lodash/debounce';

import {TaskProgressStepFieldType} from 'app/tasks/types';

import {StepTaskCheckboxField} from './checkbox-field';
import {StepTaskDateField} from './date-field';
import {StepTaskFileField} from './file-field';
import {StepTaskRadiobuttonField} from './radiobutton-field';
import {StepTaskTextField} from './text-field';
import {StepTaskTextareaField} from './textarea-field';

import type {AttachedFile, StepFieldType} from 'sweet-types';

interface Props {
  progress?: TaskProgressStepFieldType;
  field: StepFieldType;
  blocked: boolean;
  inputListener: (id: string, value: any) => Promise<any>;
}
type State = {
  value: string;
  isSaving: boolean;
};

const getProgressValue = (props: Props) => {
  const {progress} = props;
  return progress != null && progress.value != null ? progress.value : '';
};

export class StepTaskFieldWrapper extends React.PureComponent<Props, State> {
  static defaultProps = {
    blocked: false,
  };
  state = {
    value: '',
    isSaving: false,
  };
  fields = {
    text: StepTaskTextField,
    textarea: StepTaskTextareaField,
    checkbox: StepTaskCheckboxField,
    date: StepTaskDateField,
    radio: StepTaskRadiobuttonField,
    file: StepTaskFileField,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      value: getProgressValue(props),
      isSaving: false,
    };
  }

  dateFieldValueChanged = (date: Date | null) => {
    this.setState(
      {
        value: date != null ? date.toISOString() : '',
      },
      this.onSubmit
    );
  };
  textFieldValueChanged = (event) => {
    this.setState({
      value: event.target.value,
    });
    this.debouncedOnSubmit();
  };
  textareaFieldValueChanged = (value: string) => {
    this.setState({
      value,
    });
    this.debouncedOnSubmit();
  };
  checkboxFieldValueChanged = (value: string) => {
    // checkbox is a comma separated list of the selected ids.
    // toggles on and off when you repeatedly send 'value'
    const values = new Set(this.state.value.split(','));

    if (this.state.value.indexOf(value) !== -1) {
      values.delete(value);
    } else {
      values.add(value);
    }

    const newvalue = Array.from(values).join(',');
    this.setState({
      isSaving: true,
      value: newvalue,
    });
    this.submitValue(newvalue);
  };
  radiobuttonFieldValueChanged = (value: string) => {
    this.setState({
      isSaving: true,
      value,
    });
    this.submitValue(value);
  };
  fileFieldValueChanged = (value: string) => {
    this.setState({
      isSaving: true,
      value,
    });
    this.submitValue(value);
  };
  onSubmit = () => {
    this.debouncedOnSubmit.cancel();
    const propsValue = getProgressValue(this.props);

    if (propsValue !== this.state.value) {
      this.setState({
        isSaving: true,
      });
      this.submitValue(this.state.value);
    }
  };

  componentDidUpdate(prevProps: Props) {
    const progress = getProgressValue(this.props);

    if (progress !== getProgressValue(prevProps)) {
      this.setState({
        value: progress,
        isSaving: false,
      });
    }
  }

  debouncedOnSubmit = debounce(this.onSubmit, 2000);

  submitValue(value: string | AttachedFile) {
    const {inputListener, field} = this.props;
    return inputListener(field.id, value).then(
      () =>
        this.setState({
          isSaving: false,
        }),
      () =>
        this.setState({
          isSaving: false,
        })
    );
  }

  render() {
    const {progress, field, blocked} = this.props;
    const {value, isSaving} = this.state;
    const initialValue = getProgressValue(this.props);
    const InputElement = this.fields[field.type] || StepTaskTextField;
    return (
      <li className="list-group-item">
        <InputElement
          blocked={blocked}
          value={value}
          initial={initialValue}
          isSaving={isSaving}
          progress={progress}
          field={field}
          onSubmit={this.onSubmit}
          inputValueChanged={
            field.type === 'checkbox'
              ? this.checkboxFieldValueChanged
              : field.type === 'date'
              ? this.dateFieldValueChanged
              : field.type === 'radio'
              ? this.radiobuttonFieldValueChanged
              : field.type === 'file'
              ? this.fileFieldValueChanged
              : field.type === 'textarea'
              ? this.textareaFieldValueChanged
              : this.textFieldValueChanged
          }
        />
      </li>
    );
  }
}
